<script setup lang="ts">
import { computed } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaDeleteButton from '../base/GaiaDeleteButton.vue';
import GaiaTag from '../base/GaiaTag.vue';
import { gaiaGlyphPaths } from '../glyphs';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  /** Neutral chip after the title, e.g. the response or evaluation type. */
  typeLabel: {
    type: String,
    default: undefined,
  },
  /** Accent chip with an arrow, e.g. "3 evaluations" on a group. */
  countLabel: {
    type: String,
    default: undefined,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  /** Swaps the title for an input and flips the edit button to "Done". */
  editing: {
    type: Boolean,
    default: false,
  },
  /** Tooltip for the optional ＋ control, e.g. "Add outcome". Omit to hide it. */
  addLabel: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  toggle: [];
  edit: [];
  remove: [];
  add: [];
  'update:title': [title: string];
}>();

const caret = computed(() => (props.expanded ? 'M2.5 4.5 6 8l3.5-3.5' : 'M4.5 2.5 8 6l-3.5 3.5'));

function onTitle(event: Event): void {
  emit('update:title', (event.target as HTMLInputElement).value);
}

const border = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
const soft = gaiaNeutral.surfaceSoft;
const ink = gaiaNeutral.ink;
const subtle = gaiaNeutral.textSubtle;
const inputBorder = gaiaNeutral.border;
const hoverStrong = gaiaNeutral.surfaceHoverStrong;
// The title is a button and the rename input a native field, so neither can
// take GaiaText props — their type comes through v-bind.
const titleSize = gaiaFontSize.prose;
const semibold = String(gaiaFontWeight.semibold);
const bold = String(gaiaFontWeight.bold);
const displayTracking = gaiaTracking.display;
</script>

<template>
  <!-- Expandable item card shared by every playbook step (Extractions,
       Evaluations, Uncommons, Clauses, Groups). The body slot holds the
       GaiaSubPanel columns; the header owns caret, chips and row actions. -->
  <div class="gaia-playbook-item" :class="{ editing: props.editing }">
    <n-flex align="center" :size="10" :wrap="false" :style="{ padding: '11px 13px 11px 9px' }">
      <button
        type="button"
        class="icon-action"
        :aria-label="props.expanded ? 'Collapse' : 'Expand'"
        @click="emit('toggle')"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path :d="caret" />
        </svg>
      </button>

      <input
        v-if="props.editing"
        class="title-input"
        :value="props.title"
        :aria-label="`${props.title} name`"
        @input="onTitle"
      />
      <button v-else type="button" class="title" @click="emit('toggle')">{{ props.title }}</button>

      <!-- Chips that belong to the record rather than to its shape — a
           classification's group memberships, and the control that assigns
           one. They sit before the type chip so the row still ends with the
           same edit/remove pair on every step. -->
      <slot name="chips" />

      <gaia-tag v-if="props.typeLabel" size="medium">{{ props.typeLabel }}</gaia-tag>
      <gaia-tag v-if="props.countLabel" variant="accent" size="medium">
        <template #icon>
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 3v4a2 2 0 0 0 2 2h8" />
            <path d="M11 6.5 13.5 9 11 11.5" />
          </svg>
        </template>
        {{ props.countLabel }}
      </gaia-tag>

      <!-- A caller-supplied control (e.g. a group-assignment popover) sits with
           the row's own actions so every step ends with the same trio. -->
      <slot name="action" />

      <button
        v-if="props.addLabel"
        type="button"
        class="icon-action"
        :title="props.addLabel"
        :aria-label="props.addLabel"
        @click="emit('add')"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path :d="gaiaGlyphPaths.plus" />
        </svg>
      </button>
      <gaia-button size="tiny" @click="emit('edit')">
        {{ props.editing ? 'Done' : 'Edit' }}
      </gaia-button>
      <gaia-delete-button :subject="props.title" @click="emit('remove')" />
    </n-flex>

    <n-flex
      v-if="props.expanded"
      align="flex-start"
      :size="12"
      :style="{ padding: '0 13px 13px 42px' }"
    >
      <slot />
    </n-flex>
  </div>
</template>

<style scoped>
.gaia-playbook-item {
  border: 1px solid v-bind(border);
  border-radius: 12px;
  background: v-bind(surface);
  overflow: hidden;
}

.gaia-playbook-item.editing {
  border-color: v-bind(ink);
  background: v-bind(soft);
}

.title {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: v-bind(titleSize);
  font-weight: v-bind(bold);
  letter-spacing: v-bind(displayTracking);
  color: v-bind(ink);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.title-input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  height: 32px;
  padding: 0 10px;
  border: 1px solid v-bind(inputBorder);
  border-radius: 8px;
  outline: none;
  background: v-bind(surface);
  font-family: inherit;
  font-size: v-bind(titleSize);
  font-weight: v-bind(semibold);
  color: v-bind(ink);
}

.title-input:focus {
  border-color: v-bind(ink);
}

.icon-action {
  width: 26px;
  height: 26px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: none;
  color: v-bind(subtle);
  cursor: pointer;
}

.icon-action:hover {
  background: v-bind(hoverStrong);
  color: v-bind(ink);
}
</style>
